import React from 'react';
import { ArrowRight, ShieldCheck, Sparkles, Clock, FileText, Target, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

/**
 * AssessmentPromo — homepage conversion section for the premium $27 Apex Six IDP.
 *
 * Placed directly after the Apex Six (Method) section so visitors flow naturally
 * from "here are the six frameworks" → "now find out which one YOU need first."
 *
 * Visual rules (match design_guidelines.md):
 *  - Brand palette: Midnight Green (#004C54), Teal accent (#4FBCC7), cream (#FAFAF7)
 *  - One large IDP-style mock card on the right (echo of /assessment sales page)
 *  - Left-aligned editorial content (no centered paragraphs)
 *  - Single primary CTA, secondary text link
 *  - data-testid attributes on every interactive element
 */
export const AssessmentPromo = () => {
  return (
    <section
      className="relative py-20 sm:py-28 bg-[#FAFAF7]"
      data-testid="assessment-promo-section"
      aria-labelledby="assessment-promo-heading"
    >
      {/* Subtle decorative glow — kept under 20% viewport area per guidelines */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 -translate-y-1/2 right-0 w-[480px] h-[480px] rounded-full bg-[#4FBCC7]/8 blur-3xl"
      />

      <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-center">
          {/* ---------- Left: editorial copy + CTAs ---------- */}
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#004C54]/15 bg-white px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] text-[#004C54] font-bold mb-5" data-testid="assessment-promo-eyebrow">
              <Sparkles className="h-3.5 w-3.5" /> For Athletes Only · Premium · $27 IDP
            </div>

            <h2
              id="assessment-promo-heading"
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1] text-[#0a0d0e]"
              data-testid="assessment-promo-headline"
            >
              Athletes — find out which of the six
              <span className="text-[#004C54]"> you need to install first.</span>
            </h2>

            <p className="mt-5 text-base sm:text-lg text-foreground/65 leading-relaxed max-w-[58ch]" data-testid="assessment-promo-subhead">
              The <b>Apex Six Athlete Self-Assessment</b> is built <b>exclusively for athletes</b>
              {' '}— it scores you across all six mental-performance dimensions in under 8 minutes,
              then turns the result into your personalized <b>Individual Development Plan</b>: the
              framework to install, three specific drills to run this week, and a 25% off code for
              your first 1:1 session. <span className="text-foreground/55">Parents and coaches —
              please use our free quizzes instead.</span>
            </p>

            {/* Inclusion bullets */}
            <ul className="mt-6 space-y-2.5 text-sm">
              {[
                { icon: Target, text: '6-dimension score (0–100) + coach-voice narrative' },
                { icon: Sparkles, text: 'Your #1 priority auto-prescribes its Apex Six framework' },
                { icon: FileText, text: 'Printable IDP PDF emailed to you + downloadable' },
                { icon: CheckCircle2, text: '25% off your first 1:1 session (book within 7 days)' },
              ].map((b) => (
                <li key={b.text} className="flex items-start gap-2.5" data-testid={`assessment-promo-bullet-${b.text.split(' ')[0].toLowerCase()}`}>
                  <b.icon className="h-4 w-4 text-[#4FBCC7] shrink-0 mt-0.5" />
                  <span className="text-foreground/75">{b.text}</span>
                </li>
              ))}
            </ul>

            {/* CTAs */}
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                asChild
                size="lg"
                className="h-12 px-7 rounded-full bg-[#004C54] hover:bg-[#003a40] text-white font-semibold tracking-tight"
                data-testid="assessment-promo-primary-cta"
              >
                <a href="/assessment">
                  Get my Athlete IDP · $27 <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button
                asChild
                variant="ghost"
                size="lg"
                className="h-12 px-5 text-foreground/80 hover:bg-foreground/[0.04]"
                data-testid="assessment-promo-secondary-cta"
              >
                <a href="/assessment#what-you-get">See what's inside</a>
              </Button>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-foreground/55">
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" /> 6–8 min · 18 questions
              </span>
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" /> Stripe secure · One-time
              </span>
            </div>
          </div>

          {/* ---------- Right: IDP preview card ---------- */}
          <div className="relative">
            <Card
              className="border-[#E5E7E7] shadow-[0_24px_64px_rgba(0,76,84,0.15)] overflow-hidden"
              data-testid="assessment-promo-preview-card"
            >
              <div className="bg-[#004C54] px-6 py-4">
                <div className="text-[10px] tracking-[0.2em] font-bold text-white/70 font-mono uppercase">
                  Athlete IDP · Sample
                </div>
                <div className="text-white font-semibold text-lg mt-0.5">Apex Six Mental-Performance Score</div>
              </div>
              <CardContent className="p-6">
                <div className="flex items-baseline gap-3">
                  <div className="text-5xl font-bold text-[#004C54] leading-none tabular-nums">73</div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/55 font-mono font-bold">
                      Overall
                    </div>
                    <div className="text-sm font-semibold">Solid but Uneven</div>
                  </div>
                </div>

                <div className="mt-5 space-y-2.5">
                  {[
                    ['Preparation', 88],
                    ['Focus', 80],
                    ['Resilience', 73],
                    ['Confidence', 67],
                    ['Composure', 58],
                    ['Awareness', 53],
                  ].map(([label, val]) => (
                    <div key={label} className="flex items-center gap-3" data-testid={`assessment-promo-bar-${String(label).toLowerCase()}`}>
                      <div className="w-24 text-xs font-semibold text-foreground/70">{label}</div>
                      <div className="flex-1 h-2 rounded-full bg-[#F0F4F4] overflow-hidden">
                        <div
                          className={`h-full rounded-full ${val >= 70 ? 'bg-[#4FBCC7]' : 'bg-[#004C54]'}`}
                          style={{ width: `${val}%` }}
                        />
                      </div>
                      <div className="w-8 text-right text-xs font-semibold tabular-nums">{val}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 rounded-lg bg-[#F0F4F4] p-4">
                  <div className="text-[10px] tracking-[0.2em] font-bold text-[#004C54] font-mono uppercase">
                    Primary Action
                  </div>
                  <div className="font-semibold mt-1">Awareness → Green-Yellow-Red</div>
                  <div className="text-xs text-foreground/60 mt-1">
                    + 3 specific drills to install over the next 30 days.
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AssessmentPromo;
